import { useState, useEffect, useRef } from 'react';
import { 
  User, Target, Zap, Brain, TrendingUp, AlertCircle, Check, 
  ChevronRight, RefreshCw, FileText, Star, Shield, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import DevelopmentTimeline from '@/components/DevelopmentTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Level colors
const LEVEL_COLORS = {
  focus: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  developing: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  proficient: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' }
};

// Score thresholds (must match backend)
const SCORE_THRESHOLDS = {
  proficient: 8.1,  // 8.1-10 = Proficient
  developing: 6.1,  // 6.1-8.0 = Developing
  // Below 6.1 = Focus (4.0-6.0)
};

// Radar Chart Component with Interactive Tooltips
const RadarChart = ({ data, size = 280 }) => {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, label: '', value: 0 });
  const [pointsData, setPointsData] = useState([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    
    const ctx = canvas.getContext('2d');
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = (size / 2) - 40;
    
    // Clear
    ctx.clearRect(0, 0, size, size);
    
    const labels = Object.keys(data);
    const values = Object.values(data);
    const numPoints = labels.length;
    const angleStep = (Math.PI * 2) / numPoints;
    
    // Store points data for hover detection
    const points = [];
    
    // Draw grid circles
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= 5; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (radius * i) / 5, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Draw grid lines
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(angle),
        centerY + radius * Math.sin(angle)
      );
      ctx.stroke();
    }
    
    // Draw data polygon
    ctx.beginPath();
    ctx.fillStyle = 'rgba(218, 174, 62, 0.3)';
    ctx.strokeStyle = '#daae3e';
    ctx.lineWidth = 2;
    
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const value = Math.max(0, Math.min(10, values[i])) / 10;
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);
      
      // Store point data for hover
      points.push({ x, y, label: labels[i], value: values[i] });
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw points
    ctx.fillStyle = '#daae3e';
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const value = Math.max(0, Math.min(10, values[i])) / 10;
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Draw labels
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '11px system-ui';
    ctx.textAlign = 'center';
    
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 25;
      const x = centerX + labelRadius * Math.cos(angle);
      const y = centerY + labelRadius * Math.sin(angle);
      
      // Adjust text position
      let textX = x;
      let textY = y;
      if (angle > -0.1 && angle < 0.1) textY -= 5;
      if (angle > Math.PI - 0.1 && angle < Math.PI + 0.1) textY += 10;
      
      // Split long labels
      const label = labels[i];
      if (label.includes('&')) {
        const parts = label.split(' & ');
        ctx.fillText(parts[0] + ' &', textX, textY - 6);
        ctx.fillText(parts[1], textX, textY + 6);
      } else if (label.length > 12) {
        const words = label.split(' ');
        if (words.length > 1) {
          ctx.fillText(words[0], textX, textY - 6);
          ctx.fillText(words.slice(1).join(' '), textX, textY + 6);
        } else {
          ctx.fillText(label, textX, textY);
        }
      } else {
        ctx.fillText(label, textX, textY);
      }
    }
    
    setPointsData(points);
  }, [data, size]);
  
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !pointsData.length) return;
    
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Check if mouse is near any point (within 15px radius)
    let foundPoint = null;
    for (const point of pointsData) {
      const distance = Math.sqrt(Math.pow(mouseX - point.x, 2) + Math.pow(mouseY - point.y, 2));
      if (distance < 15) {
        foundPoint = point;
        break;
      }
    }
    
    if (foundPoint) {
      setTooltip({
        visible: true,
        x: foundPoint.x,
        y: foundPoint.y - 35,
        label: foundPoint.label,
        value: foundPoint.value
      });
    } else {
      setTooltip({ ...tooltip, visible: false });
    }
  };
  
  const handleMouseLeave = () => {
    setTooltip({ ...tooltip, visible: false });
  };
  
  // Determine level based on score (using new thresholds)
  const getLevel = (value) => {
    if (value >= SCORE_THRESHOLDS.proficient) return { text: 'Proficient', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (value >= SCORE_THRESHOLDS.developing) return { text: 'Developing', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { text: 'Focus', color: 'text-red-400', bg: 'bg-red-500/20' };
  };
  
  return (
    <div className="relative">
      <canvas 
        ref={canvasRef} 
        width={size} 
        height={size}
        className="mx-auto cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      {/* Tooltip */}
      {tooltip.visible && (
        <div 
          className="absolute pointer-events-none z-10 transform -translate-x-1/2"
          style={{ 
            left: `${tooltip.x}px`, 
            top: `${tooltip.y}px`
          }}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-center min-w-[120px]">
            <p className="text-xs text-muted-foreground mb-1">{tooltip.label}</p>
            <p className="text-lg font-bold text-primary">{tooltip.value?.toFixed(1)}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded ${getLevel(tooltip.value).bg} ${getLevel(tooltip.value).color}`}>
              {getLevel(tooltip.value).text}
            </span>
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-1">
            <div className="w-2 h-2 bg-card border-r border-b border-border rotate-45"></div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * PlayerProfileView
 * Complete player profile with compiled evaluation data
 */
const PlayerProfileView = ({ athleteId, athleteName, showRadar = true }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedArea, setExpandedArea] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);

  useEffect(() => {
    if (athleteId) {
      fetchProfile();
    }
  }, [athleteId]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, timelineRes] = await Promise.all([
        fetch(`${API_URL}/api/evaluations/athlete/${athleteId}/profile`),
        fetch(`${API_URL}/api/evaluations/athlete/${athleteId}/timeline`).catch(() => null)
      ]);
      const data = await profileRes.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setProfile(data);
      }
      
      if (timelineRes) {
        const tData = await timelineRes.json();
        setTimeline(tData.history || []);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
      setTimelineLoading(false);
    }
  };

  const forceRecompile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/evaluations/athlete/${athleteId}/compile`, {
        method: 'POST'
      });
      const data = await response.json();
      setProfile(data);
    } catch (err) {
      console.error('Error recompiling:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <RefreshCw className="animate-spin mx-auto text-primary mb-4" size={32} />
        <p className="text-muted-foreground">Loading player profile...</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <AlertCircle className="mx-auto text-muted-foreground mb-4" size={48} />
        <h3 className="text-lg font-medium mb-2">No Evaluation Data</h3>
        <p className="text-muted-foreground text-sm mb-4">
          {error || 'This athlete has not been evaluated yet.'}
        </p>
      </div>
    );
  }

  const getAreaIcon = (area) => {
    switch (area) {
      case 'technical': return <Target size={18} />;
      case 'physical': return <Zap size={18} />;
      case 'game_intelligence': return <Brain size={18} />;
      default: return <Star size={18} />;
    }
  };

  const getAreaName = (area) => {
    switch (area) {
      case 'technical': return 'Technical';
      case 'physical': return 'Physical';
      case 'game_intelligence': return 'Tactical IQ';
      default: return area;
    }
  };

  // Group skills by category
  const skillsByCategory = {
    technical: profile.compiled_skills?.filter(s => 
      ['ball_control', 'passing', 'dribbling', 'first_touch', 'finishing', 'shooting'].includes(s.skill)
    ) || [],
    physical: profile.compiled_skills?.filter(s => 
      ['speed', 'acceleration', 'agility', 'coordination', 'balance', 'explosiveness', 'endurance'].includes(s.skill)
    ) || [],
    game_intelligence: profile.compiled_skills?.filter(s => 
      ['positioning', 'decision_making', 'vision', 'game_awareness', 'reaction_time'].includes(s.skill)
    ) || []
  };

  return (
    <div className="space-y-6">
      {/* Info Card - How this profile is generated */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
            <FileText className="text-primary" size={20} />
          </div>
          <div>
            <h3 className="font-heading text-lg mb-2">How this profile is generated</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This development profile is generated from evaluations provided by your coaches and analyzed 
              through the Meybra AI system. The platform compiles observations from training environments 
              and match situations to create a structured overview of the player's technical, physical, 
              and game intelligence development.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              This profile is designed to support long-term player development by highlighting strengths, 
              identifying areas for improvement, and tracking progress over time.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <User size={32} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-heading">{profile.athlete_name || athleteName}</h2>
              <p className="text-primary font-medium">{profile.player_type}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Overall Score</p>
            <p className="text-4xl font-bold text-primary">{profile.overall_score?.toFixed(1)}</p>
          </div>
        </div>

        {/* Evaluation Sources */}
        <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Private Coach:</span>
            {profile.evaluation_sources?.private_coach ? (
              <span className="flex items-center gap-1 text-emerald-400 text-sm">
                <Check size={14} /> Evaluated
                {profile.evaluation_reliability?.private_coach && profile.evaluation_reliability.private_coach !== 'n/a' && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 ${
                    profile.evaluation_reliability.private_coach === 'high' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : profile.evaluation_reliability.private_coach === 'medium'
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`} data-testid="private-coach-reliability-badge">
                    {profile.evaluation_reliability.private_coach === 'high' && <ShieldCheck size={12} />}
                    {profile.evaluation_reliability.private_coach === 'medium' && <Shield size={12} />}
                    {profile.evaluation_reliability.private_coach === 'high' 
                      ? 'Verified' 
                      : profile.evaluation_reliability.private_coach}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground text-sm">Not evaluated</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Team Coach:</span>
            {profile.evaluation_sources?.team_coach ? (
              <span className="flex items-center gap-1 text-emerald-400 text-sm">
                <Check size={14} /> Evaluated
                {profile.evaluation_reliability?.team_coach && profile.evaluation_reliability.team_coach !== 'n/a' && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 ${
                    profile.evaluation_reliability.team_coach === 'high' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : profile.evaluation_reliability.team_coach === 'medium'
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`} data-testid="team-coach-reliability-badge">
                    {profile.evaluation_reliability.team_coach === 'high' && <ShieldCheck size={12} />}
                    {profile.evaluation_reliability.team_coach === 'medium' && <Shield size={12} />}
                    {profile.evaluation_reliability.team_coach === 'high' 
                      ? 'Verified' 
                      : profile.evaluation_reliability.team_coach}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground text-sm">Not evaluated</span>
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">Profile Completeness:</span>
            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${profile.profile_completeness || 0}%` }}
              />
            </div>
            <span className="text-sm font-medium">{profile.profile_completeness}%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Radar Chart & Scores */}
        <div className="lg:col-span-2 space-y-6">
          {/* Radar Chart */}
          {showRadar && profile.radar_data && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-heading mb-4">Player Radar</h3>
              <RadarChart data={profile.radar_data} size={300} />
            </div>
          )}

          {/* Area Scores */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading mb-4">Area Breakdown</h3>
            <div className="space-y-4">
              {Object.entries(profile.area_scores || {}).map(([area, score]) => (
                <div key={area}>
                  <button
                    onClick={() => setExpandedArea(expandedArea === area ? null : area)}
                    className="w-full flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center text-primary">
                        {getAreaIcon(area)}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{getAreaName(area)}</p>
                        <p className="text-sm text-muted-foreground">
                          {skillsByCategory[area]?.length || 0} skills
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-primary">{score?.toFixed(1)}</span>
                      <ChevronRight 
                        size={20} 
                        className={`text-muted-foreground transition-transform ${
                          expandedArea === area ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expanded Skills */}
                  {expandedArea === area && (
                    <div className="mt-2 ml-4 space-y-2">
                      {skillsByCategory[area]?.map(skill => (
                        <div 
                          key={skill.skill}
                          className="flex items-center justify-between p-3 bg-background rounded-lg border border-border"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{skill.skill_name}</span>
                              {skill.has_divergence && (
                                <AlertCircle size={14} className="text-amber-400" title="Divergence between coaches" />
                              )}
                            </div>
                            {skill.compiled_note && (
                              <p className="text-xs text-muted-foreground mt-1">{skill.compiled_note}</p>
                            )}
                            {skill.has_divergence && (
                              <div className="flex gap-2 mt-1">
                                <span className="text-xs text-muted-foreground">
                                  Private: <span className={LEVEL_COLORS[skill.private_level]?.text}>{skill.private_level}</span>
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  Team: <span className={LEVEL_COLORS[skill.team_level]?.text}>{skill.team_level}</span>
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              LEVEL_COLORS[skill.level]?.bg || ''
                            } ${LEVEL_COLORS[skill.level]?.text || ''}`}>
                              {skill.level?.charAt(0).toUpperCase() + skill.level?.slice(1)}
                            </span>
                            <span className="text-lg font-bold">{skill.score?.toFixed(1)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Strengths, Development, Insights */}
        <div className="space-y-6">
          {/* AI Profile Summary (if available) */}
          {profile.ai_profile_summary && (
            <div className="bg-gradient-to-r from-primary/5 to-transparent border border-primary/30 rounded-lg p-6">
              <h3 className="font-heading mb-3 flex items-center gap-2">
                <span className="text-primary">✨</span>
                AI Profile Summary
              </h3>
              <p className="text-sm leading-relaxed">{profile.ai_profile_summary}</p>
              {profile.ai_player_type_reasoning && (
                <p className="text-xs text-muted-foreground mt-3 italic">
                  "{profile.ai_player_type_reasoning}"
                </p>
              )}
            </div>
          )}

          {/* Development Timeline */}
          <DevelopmentTimeline history={timeline} loading={timelineLoading} />

          {/* Strengths */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading mb-4 flex items-center gap-2">
              <TrendingUp className="text-emerald-400" size={18} />
              Strengths
              {profile.ai_generated && <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded ml-2">AI Enhanced</span>}
            </h3>
            {profile.strengths?.length > 0 ? (
              <div className="space-y-2">
                {profile.strengths.map((strength, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-2 p-2 bg-emerald-500/10 rounded-lg"
                  >
                    <Check size={14} className="text-emerald-400" />
                    <span className="text-sm">{strength}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No proficient skills identified yet.</p>
            )}
          </div>

          {/* Development Priorities */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading mb-4 flex items-center gap-2">
              <Target className="text-amber-400" size={18} />
              Development Priorities
              {profile.ai_generated && <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded ml-2">AI Enhanced</span>}
            </h3>
            {profile.development_priorities?.length > 0 ? (
              <div className="space-y-2">
                {profile.development_priorities.map((priority, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-2 p-2 bg-amber-500/10 rounded-lg"
                  >
                    <AlertCircle size={14} className="text-amber-400" />
                    <span className="text-sm">{priority}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No critical focus areas identified.</p>
            )}
          </div>

          {/* Coach Insight Synthesis (AI) */}
          {profile.ai_coach_insight_synthesis && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-heading mb-4 flex items-center gap-2">
                <span className="text-primary">🎯</span>
                Coach Insight Analysis
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {profile.ai_coach_insight_synthesis}
              </p>
            </div>
          )}

          {/* Insights */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading mb-4 flex items-center gap-2">
              <FileText className="text-primary" size={18} />
              Insights
            </h3>
            {profile.insights?.length > 0 ? (
              <div className="space-y-3">
                {profile.insights.map((insight, idx) => (
                  <p key={idx} className="text-sm text-muted-foreground">
                    • {insight}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No insights available.</p>
            )}
          </div>

          {/* Recompile Button */}
          <Button 
            onClick={forceRecompile}
            variant="outline"
            className="w-full"
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Recompile Profile {profile.ai_generated ? '(with AI)' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlayerProfileView;
